import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { filterArrayByArray } from "../../../../helpers/filterArrayByArray";
import Api from "../../../../api";
import SVG from "../../../../components/SVG";
import history from "../../../../router/history";

import { Button, Modal, LoadingIndicator } from "../../../../components/simple";
import ButtonDeleteRegion from "./ButtonDeleteRegion";
import AddZipForRegion from "./AddZipForRegion";
import AudienceMap from "../../../../components/complex/AudinceMap/AudienceMap";
import styles from "./locations-builder.module.scss";

const LocationlesBuilderModal = ({
  selectedRegions,
  regionsList,
  onSelect,
  toggle,
  isOpen,
  isLoading,
  isLoadingMap,
  isLoadingRegions,
  country,
  isMapLayoutLoading
  // mapLayouts
}) => {
  const [selected, setSelected] = useState(selectedRegions);
  const [mapData, setMapData] = useState([]);
  const [isLoadingRegion, setIsLoadingRegion] = useState(false);
  const [dataForDraw, setDataForDraw] = useState({
    type: "FeatureCollection",
    features: []
  });
  const [alreadyKnownSets, setAlreadyKnownSets] = useState([]);
  const [isLoadingRegionsData, setIsloadingRegionsData] = useState(false);

  const { mapboxCenterLat, mapboxCenterLon, mapboxDefaultZoom } = country;

  useEffect(() => {
    const features =
      mapData.reduce((accumulator, currentFeature) => {
        if (currentFeature.elements) {
          return [...accumulator, ...currentFeature.elements];
        }
        return [...accumulator, ...currentFeature];
      }, []) || [];
    if (features.length) {
      setDataForDraw({
        type: "FeatureCollection",
        features
      });
    } else {
      setDataForDraw({
        type: "FeatureCollection",
        features: []
      });
    }
  }, [mapData]);

  const getRegions = async arr => {
    setIsloadingRegionsData(true);
    const regionsFetchFor = filterArrayByArray(arr, alreadyKnownSets, "id");
    const res = await Promise.all(
      regionsFetchFor.map(async item => {
        const { id } = item;
        const reg = await Api.audience.regionMap({
          data: { id }
        });
        return {
          id,
          elements: reg
        };
      })
    );
    setMapData([...alreadyKnownSets, ...res]);
    setAlreadyKnownSets([...alreadyKnownSets, ...res]);
    setSelected(arr);
    setIsloadingRegionsData(false);
  };

  const getOneRegion = async item => {
    // Check if user already fetched polygons for selected item
    const alreadyKnownSet = alreadyKnownSets.find(set => set.id === item.id);
    if (alreadyKnownSet) {
      setMapData([...mapData, alreadyKnownSet]);
    } else {
      // fetch data if he haven't done it yet
      setIsLoadingRegion(true);
      const res = await Api.audience.regionMap({
        data: { id: item.id }
      });
      setIsLoadingRegion(false);
      // update data and keep it for next usage
      setAlreadyKnownSets([...alreadyKnownSets, res]);
      setMapData([...mapData, { id: item.id, elements: res }]);
    }
  };

  useEffect(() => {
    if (!selected.length) {
      getRegions(selectedRegions);
    }
    // eslint-disable-next-line
  }, [selectedRegions]);

  useEffect(() => {
    if (selected.length) {
      getRegions(selected);
    }

    // eslint-disable-next-line
  }, []);

  const onSelected = async item => {
    if (selected.some(it => it.id === item.id)) {
      const newData = selected.filter(it => it.id !== item.id);
      const newDataMap = mapData.filter(it => it.id !== item.id);
      setMapData(newDataMap);
      setSelected(newData);
    } else {
      await getOneRegion(item);
      setSelected([...selected, item]);
    }
  };

  const handleSubmit = () => {
    onSelect(selected);
    toggle();
  };

  const selectAll = () => {
    if (selected.length !== regionsList.length) {
      getRegions(regionsList);
    } else {
      setSelected([]);
      setMapData([]);
    }
  };

  const onRemove = async it => {
    await Api.audience.deleteRegion({
      data: { id: it.id }
    });
    const check = selectedRegions.filter(item => item.id === it.id);
    if (check.length) {
      history.push("/audiences");
    }
    const select = selected.some(item => item.id === it.id);
    if (select) {
      onSelected(it);
    }
  };

  const getOwnRegionList = () => {
    return regionsList.filter(item => item.custom);
  };

  const onAddZip = async file => {
    file.set("country_id", country.id);

    const res = await Api.audience.addRegion({
      data: file
    });

    if (res?.payload) {
      return res?.payload;
    }
    return "";
  };

  return (
    <Modal className={`screen-modal ${styles.modal}`} isOpen={isOpen}>
      <div className={styles.header}>
        <h5>Add locations</h5>
        <div className={styles.buttonWrapper}>
          <Button
            className={`${styles.button} ${styles["button-close"]}`}
            onClick={toggle}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} className={`button ${styles.button}`}>
            ADD
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.map}>
          <AudienceMap
            isMapLayoutLoading={isMapLayoutLoading}
            mapLayout={dataForDraw}
            mapCenter={{
              lat: mapboxCenterLat,
              lng: mapboxCenterLon
            }}
            mapZoom={mapboxDefaultZoom}
          />
        </div>
        <div className={styles["list-container"]}>
          <div className={styles["title-wrapper"]}>
            <h5 className={styles.title}>Regions</h5>
            <AddZipForRegion onAddZip={onAddZip} />
          </div>
          {/*   eslint-disable-next-line */}
          <p className={styles["button-all"]} onClick={selectAll}>
            {selected.length !== regionsList.length
              ? "Mark all regions"
              : "Unmark all regions"}
          </p>
          <div className="row">
            {regionsList
              .filter(item => !item.custom)
              .map(item => {
                return (
                  <div className={`col-3 ${styles["mb-15"]}`} key={item.id}>
                    <div
                      className={`${styles.item} ${mapData.some(
                        it => it.id === item.id
                      ) && styles.selected}`}
                    >
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                      <div
                        className={styles.text}
                        onClick={() => onSelected(item)}
                      >
                        {item.name}
                      </div>
                    </div>
                  </div>
                );
              })}

            {isLoadingRegion && (
              <div className="loader-container">
                <LoadingIndicator />
              </div>
            )}
          </div>

          {getOwnRegionList().length > 0 ? (
            <div className={styles["custome-item-container"]}>
              <div className="row">
                <div className="col">
                  <h6 className={styles.title}>Own regions</h6>
                </div>
              </div>
              <div className="row">
                {getOwnRegionList().map(item => {
                  return (
                    <div className={`col-3 ${styles["mb-15"]}`} key={item.id}>
                      <div
                        className={`${styles.item} ${styles["item-own"]} ${
                          styles["item-own-wrapper"]
                        } ${mapData.some(it => it.id === item.id) &&
                          styles.selected}`}
                      >
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                        <div
                          className={styles.text}
                          onClick={() => onSelected(item)}
                        >
                          {item.name}
                        </div>

                        <ButtonDeleteRegion
                          className={styles["item-remove"]}
                          id={item.id}
                          onClick={() => {
                            onRemove(item);
                          }}
                        >
                          <SVG.Close width={20} height={20} />
                        </ButtonDeleteRegion>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {!isLoading &&
      !isLoadingMap &&
      !isLoadingRegions &&
      !isMapLayoutLoading &&
      !isLoadingRegionsData &&
      Object.keys(country).length !== 0 ? null : (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "white"
          }}
        >
          <LoadingIndicator />
        </div>
      )}
    </Modal>
  );
};

LocationlesBuilderModal.propTypes = {
  selectedRegions: PropTypes.arrayOf(PropTypes.shape({})),
  regionsList: PropTypes.arrayOf(PropTypes.shape({})),
  onSelect: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  isLoadingMap: PropTypes.bool,
  isLoadingRegions: PropTypes.bool,
  country: PropTypes.shape({
    id: PropTypes.string,
    mapboxCenterLat: PropTypes.number,
    mapboxCenterLon: PropTypes.number,
    mapboxDefaultZoom: PropTypes.number
  }),
  isMapLayoutLoading: PropTypes.bool
  // mapLayouts: PropTypes.shape({})
};

LocationlesBuilderModal.defaultProps = {
  selectedRegions: [],
  regionsList: [],
  isOpen: false,
  isLoading: false,
  isLoadingMap: false,
  isLoadingRegions: true,
  country: {
    id: ""
  },
  isMapLayoutLoading: false
  // mapLayouts: {}
};

const mapStateToProps = ({ audiences, audienceSlice }) => {
  return {
    isLoadingMap: audiences.isLoadingMap,
    isLoading: audiences.isLoading,
    isLoadingRegions: audiences.isLoadingRegions,
    // mapLayouts: audiences.regionsMap,

    isMapLayoutLoading: audienceSlice.isMapLayoutLoading
  };
};

export default connect(mapStateToProps, null)(LocationlesBuilderModal);
